/**
 * Valid Categories Reference for Frontend
 * This should match the CategoryValidator in the backend exactly
 * 
 * IMPORTANT: This file should be kept in sync with draft-edtech-project/utils/category_validator.py
 * Any changes to valid categories should be made in both places.
 */

// Complete list of all valid categories (must match CategoryValidator.VALID_CATEGORIES)
export const VALID_CATEGORIES = new Set([
  // English Categories (Standard/Irish/Global)
  "Shakespeare",
  "Poetry", 
  "Comparative",
  "Composition",
  "Comprehension",  // Currently commented out but keeping for completeness

  // GCSE English (UK)
  "GCSE_Shakespeare",
  "GCSE_19th", 
  "GCSE_Inspector",
  "GCSE_Poetry",
  // Added: GCSE English Language writing tasks
  "GCSE_EngLang_AQA_P1_Q5",
  "GCSE_EngLang_AQA_P2_Q5",

  // HSC English (Australian Non-Victoria)
  "English_HSC_Texts",
  "English_HSC_Textual", 
  "English_HSC_Critical",
  "HSC_Craft_Selection",
  "English_HSC_CraftA",
  "English_HSC_CraftB",

  // VCE English (Australian Victoria)
  "VCE_English_SectionA",
  "VCE_English_SectionB", 
  "VCE_English_SectionC",

  // Legacy/Admin English Categories
  "Australian_HSC",
  "Australian_Textual",
  "Australian_Critical", 
  "English_HSC_Craft",

  // Added: A-Level English Literature
  "Alevel_EngLitA_AQA_Love",
  "Alevel_EngLitB_AQA_Literary",

  // Irish Language Categories
  "Aiste",
  "IE_Oral",
  "Grammar Checker",
  "Filiocht",  // Main category that leads to poem selection
  "Filiocht_I",
  "Filiocht_II",
  "Filiocht_III",

  // French Categories (Standard)
  "FR_Oral",
  "FR_Diary",
  "FR_Opinion", 
  "FR_Email",
  "FR_Recit",
  "FR_Grammar",

  // French GCSE (UK)
  "GCSE_French_Q1",
  "GCSE_French_Q2",
  "GCSE_FR_Grammar",

  // Spanish Categories (Standard)
  "ES_Oral",
  "ES_Opinion",
  "ES_LetterEmail",
  "ES_Diary", 
  "ES_Note",
  "ES_Grammar",

  // Spanish GCSE (UK)
  "GCSE_Spanish_Q1",
  "GCSE_Spanish_Q2",
  "GCSE_ES_Grammar",

  // German Categories (Standard)
  "DE_Oral",
  "DE_Ausserung",
  "DE_Letter",
  "DE_TopicalQuestions",
  "DE_Grammar",

  // German GCSE (UK)
  "GCSE_German_Q1",
  "GCSE_German_Q2", 
  "GCSE_DE_Grammar",

  // Mathematics Categories
  "MATH_Concepts",
  "MATH_Applications",

  // Science Categories (Quiz format)
  "Quiz",  // Used for Biology, Chemistry, Physics, Business, Legal Studies, Psychology

  // Special Categories
  "Custom",  // For "Revise Anything!" functionality
]);

// Categories grouped by subject type
export const ENGLISH_CATEGORIES = new Set([
  "Poetry", "Shakespeare", "Comparative", "Composition", "Comprehension",
  "GCSE_Shakespeare", "GCSE_19th", "GCSE_Inspector", "GCSE_Poetry",
  // Added: GCSE English Language writing tasks
  "GCSE_EngLang_AQA_P1_Q5", "GCSE_EngLang_AQA_P2_Q5",
  "English_HSC_Texts", "English_HSC_Textual", "English_HSC_Critical", 
  "HSC_Craft_Selection", "English_HSC_CraftA", "English_HSC_CraftB",
  "VCE_English_SectionA", "VCE_English_SectionB", "VCE_English_SectionC",
  "Australian_HSC", "Australian_Textual", "Australian_Critical", "English_HSC_Craft",
  // Added: A-Level English Literature
  "Alevel_EngLitA_AQA_Love", "Alevel_EngLitB_AQA_Literary"
]);

export const IRISH_CATEGORIES = new Set([
  "Aiste", "IE_Oral", "Grammar Checker", "Filiocht", 
  "Filiocht_I", "Filiocht_II", "Filiocht_III"
]);

export const FRENCH_CATEGORIES = new Set([
  "FR_Oral", "FR_Diary", "FR_Opinion", "FR_Email", "FR_Recit", "FR_Grammar",
  "GCSE_French_Q1", "GCSE_French_Q2", "GCSE_FR_Grammar"
]);

export const SPANISH_CATEGORIES = new Set([
  "ES_Oral", "ES_Opinion", "ES_LetterEmail", "ES_Diary", "ES_Note", "ES_Grammar",
  "GCSE_Spanish_Q1", "GCSE_Spanish_Q2", "GCSE_ES_Grammar"
]);

export const GERMAN_CATEGORIES = new Set([
  "DE_Oral", "DE_Ausserung", "DE_Letter", "DE_TopicalQuestions", "DE_Grammar",
  "GCSE_German_Q1", "GCSE_German_Q2", "GCSE_DE_Grammar"
]);

export const MATH_CATEGORIES = new Set([
  "MATH_Concepts", "MATH_Applications"
]);

export const QUIZ_CATEGORIES = new Set([
  "Quiz"  // Used across multiple subjects
]);

export const GRAMMAR_ONLY_CATEGORIES = new Set([
  "FR_Grammar", "ES_Grammar", "DE_Grammar", "Grammar Checker",
  "GCSE_FR_Grammar", "GCSE_ES_Grammar", "GCSE_DE_Grammar"
]);

export const GCSE_LANGUAGE_WORD_COUNT_CATEGORIES = new Set([
  "GCSE_French_Q1", "GCSE_French_Q2",
  "GCSE_Spanish_Q1", "GCSE_Spanish_Q2", 
  "GCSE_German_Q1", "GCSE_German_Q2"
]);

// Categories that require special handling
export const AUSTRALIAN_HSC_CATEGORIES = new Set([
  "Australian_HSC", "Australian_Textual", "Australian_Critical",
  "English_HSC_Textual", "English_HSC_Critical", "English_HSC_Texts", 
  "English_HSC_Craft", "English_HSC_CraftA", "English_HSC_CraftB"
]);

export const VCE_CATEGORIES = new Set([
  "VCE_English_SectionA", "VCE_English_SectionB", "VCE_English_SectionC"
]);

export const FILIOCHT_CATEGORIES = new Set([
  "Filiocht", "Filiocht_I", "Filiocht_II", "Filiocht_III"
]);

/**
 * Validate that a category is in the approved list
 * @param {string} category - The category string to validate
 * @returns {boolean} True if category is valid, false otherwise
 */
export function validateCategory(category) {
  if (!category || typeof category !== 'string') {
    return false;
  }
  return VALID_CATEGORIES.has(category);
}

/**
 * Validate a category strictly and throw an error if invalid
 * @param {string} category - The category string to validate
 * @throws {Error} If the category is not valid
 */
export function validateCategoryStrict(category) {
  if (!category || typeof category !== 'string') {
    throw new Error(`Category must be a string, got ${typeof category}: ${category}`);
  }
  
  if (!category.trim()) {
    throw new Error('Category cannot be empty');
  }
  
  if (!VALID_CATEGORIES.has(category)) {
    const suggestions = findSimilarCategories(category);
    let errorMsg = `Invalid category: '${category}'. This category is not in the approved list.`;
    
    if (suggestions.length > 0) {
      errorMsg += ` Did you mean one of: ${suggestions.join(', ')}?`;
    }
    
    throw new Error(errorMsg);
  }
}

/**
 * Find categories that are similar to the given invalid category
 * @param {string} category - The invalid category
 * @param {number} maxSuggestions - Maximum number of suggestions to return
 * @returns {string[]} Array of similar valid categories
 */
export function findSimilarCategories(category, maxSuggestions = 3) {
  const categoryLower = category.toLowerCase();
  const suggestions = [];
  
  for (const validCat of VALID_CATEGORIES) {
    const validLower = validCat.toLowerCase();
    
    // Check for partial matches
    if (categoryLower.includes(validLower) || 
        validLower.includes(categoryLower) ||
        category.split('_').some(part => part.length > 2 && validLower.includes(part.toLowerCase()))) {
      suggestions.push(validCat);
      
      if (suggestions.length >= maxSuggestions) {
        break;
      }
    }
  }
  
  return suggestions;
}

/**
 * Get the subject type for a validated category
 * @param {string} category - A valid category string
 * @returns {string|null} The subject type as a string, or null if category is invalid
 */
export function getCategoryType(category) {
  if (!validateCategory(category)) {
    return null;
  }
  
  if (ENGLISH_CATEGORIES.has(category)) {
    return "English";
  } else if (IRISH_CATEGORIES.has(category)) {
    return "Irish";
  } else if (FRENCH_CATEGORIES.has(category)) {
    return "French";
  } else if (SPANISH_CATEGORIES.has(category)) {
    return "Spanish";
  } else if (GERMAN_CATEGORIES.has(category)) {
    return "German";
  } else if (MATH_CATEGORIES.has(category)) {
    return "Mathematics";
  } else if (QUIZ_CATEGORIES.has(category)) {
    return "Quiz";
  } else if (category === "Custom") {
    return "Custom";
  } else {
    return "Unknown";
  }
}

/**
 * Check if a category is grammar-only
 * @param {string} category - The category to check
 * @returns {boolean} True if grammar-only category
 */
export function isGrammarOnlyCategory(category) {
  validateCategoryStrict(category);
  return GRAMMAR_ONLY_CATEGORIES.has(category);
}

/**
 * Check if a category is an English category
 * @param {string} category - The category to check
 * @returns {boolean} True if English category
 */
export function isEnglishCategory(category) {
  validateCategoryStrict(category);
  return ENGLISH_CATEGORIES.has(category);
}

/**
 * Check if a category requires word count calculation
 * @param {string} category - The category to check
 * @returns {boolean} True if word count is required
 */
export function requiresWordCount(category) {
  validateCategoryStrict(category);
  return GCSE_LANGUAGE_WORD_COUNT_CATEGORIES.has(category);
}

/**
 * Check if a category uses Australian grading system
 * @param {string} category - The category to check
 * @returns {boolean} True if Australian category
 */
export function isAustralianCategory(category) {
  validateCategoryStrict(category);
  return AUSTRALIAN_HSC_CATEGORIES.has(category) || VCE_CATEGORIES.has(category);
}

/**
 * Check if a category is related to Filíocht (Irish poetry)
 * @param {string} category - The category to check
 * @returns {boolean} True if Filíocht category
 */
export function isFiliochtCategory(category) {
  validateCategoryStrict(category);
  return FILIOCHT_CATEGORIES.has(category);
}

/**
 * Get all valid categories as a sorted array
 * @returns {string[]} Sorted list of all valid categories
 */
export function getAllValidCategories() {
  return Array.from(VALID_CATEGORIES).sort();
}

/**
 * Get categories organized by subject
 * @returns {Object} Categories organized by subject
 */
export function getCategoriesBySubject() {
  return {
    "English": Array.from(ENGLISH_CATEGORIES).sort(),
    "Irish": Array.from(IRISH_CATEGORIES).sort(),
    "French": Array.from(FRENCH_CATEGORIES).sort(),
    "Spanish": Array.from(SPANISH_CATEGORIES).sort(),
    "German": Array.from(GERMAN_CATEGORIES).sort(),
    "Mathematics": Array.from(MATH_CATEGORIES).sort(),
    "Quiz": Array.from(QUIZ_CATEGORIES).sort(),
    "Custom": ["Custom"]
  };
} 